import { FOCUS_TABS, type FocusTab } from "./components/focus/tabs";

/**
 * QW-1 — minimal hand-rolled URL sync (no react-router). The ONLY state we
 * persist to the URL is the open deal + its active tab, as `#deal/<id>/<tab>`.
 * Chat, modals, and scroll position are intentionally NOT encoded — keeps the
 * scheme small and the failure modes trivial.
 *
 * Only READY tabs are restorable. A "soon"/unbuilt tab in the URL is treated
 * like an unknown tab and falls back to overview, so a refresh never lands on
 * a placeholder.
 */
const RESTORABLE_TAB_IDS = new Set<FocusTab>(
  FOCUS_TABS.filter((t) => t.ready).map((t) => t.id),
);

export interface HashRoute {
  dealId: string;
  tab: FocusTab;
}

/**
 * Parse a location hash into a route, or null for "home".
 * - No hash / not a `deal/...` hash  → null  (edge case 3: plain home URL)
 * - `deal` with no id                → null  (malformed → home)
 * - Unknown/missing/unbuilt tab      → "overview" (edge case 2: never throws)
 * Deal-existence is NOT checked here (the parser has no deal list); the caller
 * validates the id against the loaded deals and falls back to home if it's
 * gone or inaccessible (edge case 1).
 */
export function parseHashRoute(hash: string): HashRoute | null {
  const h = hash.replace(/^#/, "");
  if (!h) return null;
  const parts = h.split("/");
  if (parts[0] !== "deal" || !parts[1]) return null;
  let dealId: string;
  try {
    dealId = decodeURIComponent(parts[1]);
  } catch {
    return null; // malformed percent-encoding → home
  }
  if (!dealId) return null;
  const rawTab = parts[2];
  const tab: FocusTab =
    rawTab && RESTORABLE_TAB_IDS.has(rawTab as FocusTab)
      ? (rawTab as FocusTab)
      : "overview";
  return { dealId, tab };
}

/** Serialize state into a hash string. Returns "" for home (no open deal). */
export function toHash(dealId: string | null, tab: FocusTab): string {
  if (!dealId) return "";
  return `#deal/${encodeURIComponent(dealId)}/${tab}`;
}
